import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { TrendingUp, TrendingDown, Minus, AlertTriangle } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  DataTable,
  EmptyState,
  LoadingState,
  ErrorState,
  PageHeader,
  Card,
  FilterBar,
  FilterChip,
  type ColumnDef,
} from '@/components/ui'
import { formatCurrency } from '../shared.ts'

// ─── Types ────────────────────────────────────────────────────────────────────

interface MarginItem {
  id: string
  itemCode: string
  itemName: string
  groupId: string | null
  groupName: string | null
  salesPriceCents: number
  purchasePriceCents: number
  marginPct: number
  grossProfitCents: number
  stockQuantity: number
  stockValueCents: number
}

interface GroupAgg {
  groupId: string | null
  groupName: string | null
  count: number
  avgMarginPct: number
  totalGrossProfitCents: number
}

interface Summary {
  totalArticles: number
  avgMarginPct: number
  totalGrossProfitCents: number
  negativeMarginCount: number
  zeroMarginCount: number
  lowMarginCount: number
}

interface MarginResponse {
  items: MarginItem[]
  byGroup: GroupAgg[]
  summary: Summary
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type MarginFilter = 'all' | 'negative' | 'zero' | 'low' | 'ok'
type ViewMode = 'articles' | 'groups'

function MarginBadge({ pct }: { pct: number }) {
  if (pct < 0) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
      <TrendingDown className="w-3 h-3" />{pct.toFixed(1)} %
    </span>
  )
  if (pct === 0) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
      <Minus className="w-3 h-3" />0 %
    </span>
  )
  if (pct < 10) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
      <AlertTriangle className="w-3 h-3" />{pct.toFixed(1)} %
    </span>
  )
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
      <TrendingUp className="w-3 h-3" />{pct.toFixed(1)} %
    </span>
  )
}

// ─── Columns ──────────────────────────────────────────────────────────────────

const articleColumns: ColumnDef<MarginItem>[] = [
  { id: 'itemCode', header: 'Art.-Nr.', accessorKey: 'itemCode', size: 110 },
  { id: 'itemName', header: 'Bezeichnung', accessorKey: 'itemName', size: 300 },
  { id: 'groupName', header: 'Gruppe', accessorKey: 'groupName', size: 160,
    cell: ({ row }) => row.original.groupName ?? <span className="text-gray-400 text-xs">–</span> },
  { id: 'purchasePriceCents', header: 'EK', accessorKey: 'purchasePriceCents', size: 110,
    cell: ({ row }) => formatCurrency(row.original.purchasePriceCents / 100) },
  { id: 'salesPriceCents', header: 'VK', accessorKey: 'salesPriceCents', size: 110,
    cell: ({ row }) => formatCurrency(row.original.salesPriceCents / 100) },
  { id: 'marginPct', header: 'Marge', accessorKey: 'marginPct', size: 110,
    cell: ({ row }) => <MarginBadge pct={row.original.marginPct} /> },
  { id: 'grossProfitCents', header: 'Rohertrag', accessorKey: 'grossProfitCents', size: 110,
    cell: ({ row }) => (
      <span className={row.original.grossProfitCents < 0 ? 'text-red-600' : ''}>
        {formatCurrency(row.original.grossProfitCents / 100)}
      </span>
    ),
  },
  { id: 'stockQuantity', header: 'Bestand', accessorKey: 'stockQuantity', size: 90 },
  { id: 'stockValueCents', header: 'Lagerwert (EK)', accessorKey: 'stockValueCents', size: 130,
    cell: ({ row }) => formatCurrency(row.original.stockValueCents / 100) },
]

const groupColumns: ColumnDef<GroupAgg>[] = [
  { id: 'groupName', header: 'Gruppe', accessorKey: 'groupName', size: 250,
    cell: ({ row }) => row.original.groupName ?? <span className="text-gray-400 text-xs">Ohne Gruppe</span> },
  { id: 'count', header: 'Artikel', accessorKey: 'count', size: 90 },
  { id: 'avgMarginPct', header: 'Ø Marge', accessorKey: 'avgMarginPct', size: 120,
    cell: ({ row }) => <MarginBadge pct={row.original.avgMarginPct} /> },
  { id: 'totalGrossProfitCents', header: 'Gesamt-Rohertrag', accessorKey: 'totalGrossProfitCents', size: 160,
    cell: ({ row }) => (
      <span className={row.original.totalGrossProfitCents < 0 ? 'text-red-600' : ''}>
        {formatCurrency(row.original.totalGrossProfitCents / 100)}
      </span>
    ),
  },
]

// ─── Section ──────────────────────────────────────────────────────────────────

export function MarginAnalysisSection() {
  const [marginFilter, setMarginFilter] = useState<MarginFilter>('all')
  const [viewMode, setViewMode] = useState<ViewMode>('articles')
  const [groupFilter, setGroupFilter] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery<MarginResponse>({
    queryKey: ['margin-analysis'],
    queryFn: () => apiClient.get('/articles/margin-analysis').then((r) => r.data),
  })

  if (isLoading) return <LoadingState label="Margen werden berechnet…" />
  if (error) {
    const axiosErr = error as { message: string; response?: { data?: { error?: string; stack?: string } } }
    return (
      <ErrorState
        title="Fehler beim Laden der Margenanalyse"
        description={axiosErr.response?.data?.error ?? axiosErr.message}
        details={axiosErr.response?.data?.stack}
      />
    )
  }

  const { items = [], byGroup = [], summary } = data ?? { items: [], byGroup: [], summary: undefined }

  const filteredItems = items.filter((i) => {
    if (groupFilter && i.groupId !== groupFilter) return false
    if (marginFilter === 'negative') return i.marginPct < 0
    if (marginFilter === 'zero') return i.salesPriceCents === 0 || i.purchasePriceCents === 0
    if (marginFilter === 'low') return i.marginPct > 0 && i.marginPct < 10
    if (marginFilter === 'ok') return i.marginPct >= 10
    return true
  })

  const uniqueGroups = Array.from(new Map(items.filter((i) => i.groupId).map((i) => [i.groupId!, i.groupName ?? i.groupId!])).entries())

  return (
    <div className="space-y-6">
      <PageHeader title="Margen-Analyse" description="Rohertrag und Marge pro Artikel und Warengruppe" />

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="cursor-pointer hover:ring-2 hover:ring-brand-300" onClick={() => { setMarginFilter('all'); setGroupFilter(null) }}>
            <Card.Body className="p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{summary.totalArticles}</div>
              <div className="text-xs text-gray-500 mt-1">Artikel gesamt</div>
            </Card.Body>
          </Card>
          <Card className="hover:ring-2 hover:ring-green-300 cursor-pointer" onClick={() => setMarginFilter('all')}>
            <Card.Body className="p-4 text-center">
              <div className="text-2xl font-bold text-green-700">{summary.avgMarginPct.toFixed(1)} %</div>
              <div className="text-xs text-gray-500 mt-1">Ø Marge</div>
            </Card.Body>
          </Card>
          <Card>
            <Card.Body className="p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalGrossProfitCents / 100)}</div>
              <div className="text-xs text-gray-500 mt-1">Gesamt-Rohertrag</div>
            </Card.Body>
          </Card>
          <Card className={`cursor-pointer hover:ring-2 hover:ring-red-300 ${marginFilter === 'negative' ? 'ring-2 ring-red-400' : ''}`} onClick={() => setMarginFilter(marginFilter === 'negative' ? 'all' : 'negative')}>
            <Card.Body className="p-4 text-center">
              <div className="text-2xl font-bold text-red-600">{summary.negativeMarginCount}</div>
              <div className="text-xs text-gray-500 mt-1">Negative Marge</div>
            </Card.Body>
          </Card>
          <Card className={`cursor-pointer hover:ring-2 hover:ring-gray-300 ${marginFilter === 'zero' ? 'ring-2 ring-gray-400' : ''}`} onClick={() => setMarginFilter(marginFilter === 'zero' ? 'all' : 'zero')}>
            <Card.Body className="p-4 text-center">
              <div className="text-2xl font-bold text-gray-600">{summary.zeroMarginCount}</div>
              <div className="text-xs text-gray-500 mt-1">Kein EK/VK</div>
            </Card.Body>
          </Card>
          <Card className={`cursor-pointer hover:ring-2 hover:ring-amber-300 ${marginFilter === 'low' ? 'ring-2 ring-amber-400' : ''}`} onClick={() => setMarginFilter(marginFilter === 'low' ? 'all' : 'low')}>
            <Card.Body className="p-4 text-center">
              <div className="text-2xl font-bold text-amber-600">{summary.lowMarginCount}</div>
              <div className="text-xs text-gray-500 mt-1">Marge &lt; 10 %</div>
            </Card.Body>
          </Card>
        </div>
      )}

      <Card>
        <Card.Body className="p-4 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
              <button
                onClick={() => setViewMode('articles')}
                className={`px-3 py-1.5 font-medium transition-colors ${viewMode === 'articles' ? 'bg-brand-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                Artikel
              </button>
              <button
                onClick={() => setViewMode('groups')}
                className={`px-3 py-1.5 font-medium transition-colors ${viewMode === 'groups' ? 'bg-brand-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                Warengruppen
              </button>
            </div>
          </div>

          {viewMode === 'articles' && uniqueGroups.length > 0 && (
            <FilterBar>
              <FilterChip
                active={groupFilter === null}
                count={items.length}
                onRemove={() => setGroupFilter(null)}
              >
                Alle Gruppen
              </FilterChip>
              {uniqueGroups.map(([id, name]) => (
                <FilterChip
                  key={id}
                  active={groupFilter === id}
                  count={items.filter((i) => i.groupId === id).length}
                  onRemove={() => setGroupFilter(groupFilter === id ? null : id)}
                >
                  {name}
                </FilterChip>
              ))}
            </FilterBar>
          )}
        </Card.Body>
      </Card>

      {viewMode === 'articles' ? (
        filteredItems.length === 0 ? (
          <EmptyState title="Keine Artikel" description="Keine Artikel mit diesen Filterkriterien gefunden." />
        ) : (
          <DataTable
            columns={articleColumns}
            data={filteredItems}
            pagination={{ pageSize: 50 }}
          />
        )
      ) : (
        byGroup.length === 0 ? (
          <EmptyState title="Keine Daten" description="Keine Gruppen-Daten verfügbar." />
        ) : (
          <DataTable
            columns={groupColumns}
            data={byGroup}
            pagination={{ pageSize: 50 }}
          />
        )
      )}
    </div>
  )
}
